import PropTypes from 'prop-types';
import React from 'react';
import Flexbox from 'flexbox-react';
import * as Table from 'reactabular-table';
import ProcessUtils from 'utils/ProcessUtils';
import Constants from 'utils/Constants';
import ProcessService from 'services/ProcessService';
import ProcessValidator from 'utils/ProcessValidator';
import AbstractLocalStorageComponent from 'components/core/AbstractLocalStorageComponent';

/**
 * Override reactabular-table Body to prevent warning message
 */
class TableBody extends Table.Body {
    render() {
        const {
            onRow, rows, rowKey, ...props
        } = this.props;
        const { columns, renderers } = this.context;

        // We are overriding to take out this ref because it causes a react warning
        // props.ref = (body) => {
        //     this.ref = body;
        // };

        return React.createElement(
            renderers.body.wrapper,
            props,
            rows.map((rowData, index) => {
                const rowIndex = rowData._index || index;
                const key = Table.resolveRowKey({ rowData, rowIndex, rowKey });

                return React.createElement(Table.BodyRow, {
                    key,
                    renderers: renderers.body,
                    onRow,
                    rowKey: key,
                    rowIndex,
                    rowData,
                    columns
                });
            })
        );
    }
}

/**
 * Table with selectable rows and embeddable EntityHeader/Toolbar for rendering some
 * subset of process data.
 */
export default class ProcessDataTable extends AbstractLocalStorageComponent {

    static propTypes = {
        tableClassName: PropTypes.string,
        className: PropTypes.string,
        process: PropTypes.object.isRequired,
        dods: PropTypes.array,
        dodsById: PropTypes.object,
        siteFacilities: PropTypes.array,
        hasWritePermissions: PropTypes.bool,
        disableSelection: PropTypes.bool,
        processErrors: PropTypes.object,
        selectable: PropTypes.bool
    };

    static defaultProps = {
        selectable: true
    };

    constructor(props) {
        super(props);
        this.state = {
            selectedItems: {}
        };
    }

    componentWillMount() {
        this._initializePropDependentVariables(this.props);
    }

    componentWillReceiveProps(nextProps) {
        this._initializePropDependentVariables(nextProps);
    }

    _initializePropDependentVariables(props) {
        this.runLocationOptions = ProcessUtils.getRuleRunLocationOptions(props.process);
        this.dataLocationOptions = ProcessUtils.getDataLocationOptions(props.siteFacilities);

        // This is the list of all datastream ids
        if (props.dods) {
            this.datastreams = Constants.convertArrayToSelectOptions(props.dods);
        } else {
            this.datastreams = [];
        }

        this.processErrors = ProcessService.getMyState(ProcessService.getStore().getState()).processErrorsById[props.process.name];

        this.rowData = this.getTableRowData(props); // we cache the data array once so we don't have to compute it multiple times

        this.validator = new ProcessValidator(props.process);
    }

    onFieldUpdated = (fieldPath, value) => {
        ProcessUtils.updateField(this.props.process, fieldPath, value);
    };

    onChange = () => {
        // do nothing
    };

    getRowNames() {
        return this.rowData.map(item => {
            return item[this.getRowKey()];
        });
    }

    toggleSelected = (rowName) => {
        let selectedItems = Object.assign({}, this.state.selectedItems);
        if (!rowName) {
            // select all toggle pressed
            if (Object.keys(selectedItems).length === this.getRowNames().length) {
                selectedItems = {}; // de-select all
            } else {
                this.getRowNames().forEach(name => {
                    selectedItems[name] = true; // select all
                });
            }

        } else {
            const selected = selectedItems[rowName] ? false : true;
            if (selected) {
                selectedItems[rowName] = true;
            } else {
                delete selectedItems[rowName];
            }
            
        }
        this.setState({ selectedItems });
    };

    isSelected = (rowName) => {
        let selected = false;
        const selectedItems = this.state.selectedItems;
        const rowNames = this.getRowNames();

        if (!rowName) {
            // select all query
            if ((Object.keys(selectedItems).length === rowNames.length) && rowNames.length !== 0) {
                selected = true;
            }

        } else {
            selected = selectedItems[rowName] ? true : false;
        }
        return selected;
    };

    /**
     * Reset the selection
     */
    clearSelection = () => {
        this.setState({ selectedItems: {} });
    };

    renderCheckbox(rowName) {
        const onClick = () => {
            this.toggleSelected(rowName);
        };
        const selected = this.isSelected(rowName);
        
        return (
            <input
                className="table-select"
                type="checkbox"
                checked={selected}
                onClick={onClick}
                onChange={this.onChange}
                style={{ width: '20px' }}
            />
        );
    }

    getSelectColumn() {
        const disableSelection = this.props.disableSelection ? true : false;
        if (disableSelection) {
            return [];
        } else {
            return [{
                property: this.getRowKey(),
                header: {
                    label: 'Selected',
                    formatters: [
                        label => { return this.renderCheckbox(); }
                    ],
                    props: {
                        style: {
                            width: 20
                        }
                    }
                },
                cell: {
                    formatters: [
                        rowKey => { return this.renderCheckbox(rowKey); }
                    ]
                }
            }];
        }
    }

    /**
     * Must be defined as an array of objects (not a map)
     */
    getTableRowData(props) {
        return []; // subclasses must override
    }

    getTableColumns() {
        return []; // subclasses must override
    }

    getRowKey() {
        return null; // subclasses must override
    }

    getSelectedItems() {
        // Filter out the items with selected === false to avoid mistakes in child components
        const selectedItems = {};
        Object.keys(this.state.selectedItems).forEach(item => {
            if (this.state.selectedItems[item]) {
                selectedItems[item] = true;
            }
        });
        return selectedItems;
    }

    renderToolbar() {
        // remember to pass this.getSelectedItems() to toolbar
        return <div />; // subclasses can override
    }

    renderFootnote() {
        return null;
    }

    renderEmptyDataMsg() {
        // subclasses can override to display message on empty data
        return null;
    }

    render() {
        const toolbar = this.renderToolbar();
        const footnote = this.renderFootnote();

        // For now we are telling reactabular-table to always update.  If performance sucks, we need to use functions
        // with more precise logic for when to update a row that includes the selection and previous selection info.
        // See https://reactabular.js.org/#/table/overriding-should-component-update
        const BodyWrapper = (props) => {
            return (<tbody {...props} />);
        };
        BodyWrapper.shouldComponentUpdate = true;
        const RowWrapper = props => <tr {...props} />;
        RowWrapper.shouldComponentUpdate = true;

        const className = this.props.className ? this.props.className : '';
        const tableClassName = this.props.tableClassName ? this.props.tableClassName : 'table table-striped table-bordered';

        let columns = null;
        if (this.props.selectable) {
            columns = this.getSelectColumn().concat(this.getTableColumns());
        } else {
            columns = this.getTableColumns();
        }

        return (
            <Flexbox className={className} flexDirection="column">
                {toolbar}
                <Table.Provider
                    className={tableClassName}
                    columns={columns}
                    renderers={
                        {
                            body: {
                                wrapper: BodyWrapper,
                                row: RowWrapper
                            }
                        }}
                >
                    <Table.Header />
                    { this.rowData.length === 0 && this.renderEmptyDataMsg() ?
                        this.renderEmptyDataMsg()
                        :
                        <TableBody rows={this.rowData} rowKey={this.getRowKey()} />
                    }
                </Table.Provider>
                {footnote}
            </Flexbox>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/core/ProcessDataTable.js